import { useMemo, useState } from "react";
import "./App.css";

// const API_URL = "http://localhost:8000/api/check-batch";
const API_URL = "https://nec.nextgendesigner.com/api/check-batch";
const MAX_FILES = 30;

function csvEscape(value) {
  const text = String(value ?? "");
  return `"${text.replaceAll('"', '""')}"`;
}

function safeBaseName(filename) {
  return filename
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_");
}

function downloadText(filename, text, mimeType = "text/csv;charset=utf-8") {
  const blob = new Blob(["\uFEFF", text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}

function buildIndividualCsv(sheet) {
  const header = [
    "Sheet",
    "Question",
    "Detected Answer",
    "Correct Answer",
    "Status",
    // "Score A",
    // "Score B",
    // "Score C",
    // "Score D",
  ];

  const rows = sheet.questions.map((question) => [
    sheet.filename,
    question.question,
    question.detectedAnswer,
    question.correctAnswer,
    question.status,
    // question.scoreA,
    // question.scoreB,
    // question.scoreC,
    // question.scoreD,
  ]);

  return [header, ...rows]
    .map((row) => row.map(csvEscape).join(","))
    .join("\n");
}

function buildCombinedDetailsCsv(results) {
  const successful = results.filter((sheet) => sheet.success);

  const header = [
    "Sheet",
    "Question",
    "Detected Answer",
    "Correct Answer",
    "Status",
    // "Score A",
    // "Score B",
    // "Score C",
    // "Score D",
  ];

  const rows = successful.flatMap((sheet) =>
    sheet.questions.map((question) => [
      sheet.filename,
      question.question,
      question.detectedAnswer,
      question.correctAnswer,
      question.status,
      // question.scoreA,
      // question.scoreB,
      // question.scoreC,
      // question.scoreD,
    ])
  );

  return [header, ...rows]
    .map((row) => row.map(csvEscape).join(","))
    .join("\n");
}

function buildSummaryCsv(results) {
  const header = [
    "Sheet",
    "Processing Status",
    "Correct",
    "Wrong",
    "Blank",
    "Multiple",
    "Percentage",
    "Threshold",
    "Error",
  ];

  const rows = results.map((sheet) => {
    if (!sheet.success) {
      return [
        sheet.filename,
        "failed",
        "",
        "",
        "",
        "",
        "",
        "",
        sheet.error,
      ];
    }

    return [
      sheet.filename,
      "success",
      sheet.summary.correct,
      sheet.summary.wrong,
      sheet.summary.blank,
      sheet.summary.multiple,
      sheet.summary.percentage,
      sheet.summary.threshold,
      "",
    ];
  });

  return [header, ...rows]
    .map((row) => row.map(csvEscape).join(","))
    .join("\n");
}

export default function OMR() {
  const [files, setFiles] = useState([]);
  const [answerKey, setAnswerKey] = useState("");
  const [threshold, setThreshold] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const answerCount = useMemo(
    () => (answerKey.toUpperCase().match(/\b[ABCD]\b/g) || []).length,
    [answerKey]
  );

  function handleFiles(event) {
    const selected = Array.from(event.target.files || []);

    setError("");
    setResponse(null);

    if (selected.length > MAX_FILES) {
      setFiles([]);
      setError(`Select at most ${MAX_FILES} answer sheets at once.`);
      event.target.value = "";
      return;
    }

    const invalid = selected.find(
      (file) => !["image/jpeg", "image/png", "image/webp"].includes(file.type)
    );

    if (invalid) {
      setFiles([]);
      setError(`Unsupported file: ${invalid.name}`);
      event.target.value = "";
      return;
    }

    setFiles(selected);
  }

  async function submitBatch(event) {
    event.preventDefault();

    setError("");
    setResponse(null);

    if (files.length === 0) {
      setError("Select at least one answer sheet.");
      return;
    }

    // if (answerCount !== 100) {
    //   setError(`Answer key must contain exactly 100 answers. Found ${answerCount}.`);
    //   return;
    // }

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("images", file, file.name);
    });

    formData.append("answer_key", answerKey);

    if (threshold.trim() !== "") {
      formData.append("threshold", threshold);
    }

    try {
      setLoading(true);

      const apiResponse = await fetch(API_URL, {
        method: "POST",
        body: formData,
      });

      const data = await apiResponse.json();

      if (!apiResponse.ok) {
        throw new Error(data.detail || "Processing failed.");
      }

      setResponse(data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }

  function downloadAllDetails() {
    if (!response) return;
    downloadText(
      "omr_all_sheets_detailed.csv",
      buildCombinedDetailsCsv(response.results)
    );
  }

  function downloadSummary() {
    if (!response) return;
    downloadText(
      "omr_batch_summary.csv",
      buildSummaryCsv(response.results)
    );
  }

  return (
    <main className="page">
      <section className="panel">
        <h1>NEA First Paper OMR Checker</h1>
        <p>
          Upload upto 30 answer sheets
        </p>

        <form onSubmit={submitBatch}>
          <label className="field">
            <span>Answer sheets ({files.length}/{MAX_FILES})</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleFiles}
            />
          </label>

          {files.length > 0 && (
            <div className="selected-files">
              {files.map((file, index) => (
                <div className="file-chip" key={`${file.name}-${index}`}>
                  {index + 1}. {file.name}
                </div>
              ))}
            </div>
          )}

          <label className="field">
            <span>Answer key ({answerCount}/100)</span>
            <textarea
              rows="8"
              value={answerKey}
              onChange={(event) => setAnswerKey(event.target.value)}
              placeholder="A,B,C,D,A,B,C,D,..."
            />
          </label>

          {/* <label className="field">
            <span>Manual threshold — optional</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={threshold}
              onChange={(event) => setThreshold(event.target.value)}
              placeholder="Automatic"
            />
          </label> */}

          <button disabled={loading}>
            {loading
              ? `Processing ${files.length} sheet(s)...`
              : `Process ${files.length || ""} sheet(s)`}
          </button>
        </form>

        {error && <div className="error-box">{error}</div>}
      </section>

      {response && (
        <>
          <BatchSummary response={response} />

          <section className="panel action-panel">
            <button type="button" onClick={downloadSummary}>
              Download summary
            </button>

            <button type="button" onClick={downloadAllDetails}>
              Download all question details
            </button>
          </section>

          <section className="results-grid">
            {response.results.map((sheet) => (
              <SheetResultCard
                key={`${sheet.id}-${sheet.filename}`}
                sheet={sheet}
              />
            ))}
          </section>
        </>
      )}
    </main>
  );
}

function BatchSummary({ response }) {
  const summary = response.batchSummary;

  return (
    <section className="panel">
      <h2>Summary</h2>

      <div className="summary-grid">
        <SummaryCard label="Uploaded" value={summary.uploaded} />
        <SummaryCard label="Processed" value={summary.processed} />
        <SummaryCard label="Failed" value={summary.failed} />
        {/* <SummaryCard label="Total correct" value={summary.totalCorrect} />
        <SummaryCard
          label="Average"
          value={`${summary.averagePercentage}%`}
        /> */}
      </div>
    </section>
  );
}

function SummaryCard({ label, value }) {
  return (
    <article className="summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function SheetResultCard({ sheet }) {
  function downloadIndividual() {
    if (!sheet.success) return;

    downloadText(
      `${safeBaseName(sheet.filename)}_result.csv`,
      buildIndividualCsv(sheet)
    );
  }

  if (!sheet.success) {
    return (
      <article className="sheet-card failed-card">
        <h2>{sheet.filename}</h2>
        <p className="failure-label">Processing failed</p>
        <p>{sheet.error}</p>
      </article>
    );
  }

  return (
    <article className="sheet-card">
      <div className="sheet-heading">
        <div>
          <h2>{sheet.filename}</h2>
          <p>
            Score: <strong>{sheet.summary.correct}/100</strong>{" "}
            ({sheet.summary.percentage}%)
          </p>
        </div>

        <button
          type="button"
          className="small-button"
          onClick={downloadIndividual}
        >
          Download CSV
        </button>
      </div>

      <div className="mini-summary">
        <span>Correct: {sheet.summary.correct}</span>
        <span>Wrong: {sheet.summary.wrong}</span>
        <span>Blank: {sheet.summary.blank}</span>
        <span>Multiple: {sheet.summary.multiple}</span>
      </div>

      <img
        className="annotated-image"
        src={sheet.annotatedImage}
        alt={`Annotated result for ${sheet.filename}`}
      />

      <details>
        <summary>Show all 100 question results</summary>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Q.</th>
                <th>Detected</th>
                <th>Correct</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {sheet.questions.map((question) => (
                <tr key={question.question}>
                  <td>{question.question}</td>
                  <td>{question.detectedAnswer}</td>
                  <td>{question.correctAnswer}</td>
                  <td>
                    <span className={`status status-${question.status}`}>
                      {question.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </article>
  );
}
